
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Language } from "@/types/language";

export interface DatabaseLanguage {
  code: string;
  name: string;
  native_name: string;
  direction: string;
  active: boolean;
  flag_icon: string;
  created_at: string;
  updated_at: string;
}

export function useLanguages(activeOnly: boolean = true) {
  const [languages, setLanguages] = useState<DatabaseLanguage[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    async function fetchLanguages() {
      try {
        setLoading(true);
        console.log(`[useLanguages] Fetching languages with activeOnly: ${activeOnly}`);
        
        let query = supabase
          .from('languages')
          .select('*')
          .order('name');
        
        if (activeOnly) {
          query = query.eq('active', true);
        }
        
        const { data, error } = await query;
        
        if (error) {
          console.error('[useLanguages] Error fetching languages:', error);
          throw error;
        }
        
        console.log(`[useLanguages] Successfully fetched ${data?.length || 0} languages`);
        setLanguages(data || []);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching languages:", err);
        setError(err);
        // Set fallback languages if database fetch fails
        const fallbackLanguages: DatabaseLanguage[] = [
          {
            code: 'en',
            name: 'English',
            native_name: 'English',
            direction: 'ltr',
            active: true,
            flag_icon: 'us',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          },
          {
            code: 'he',
            name: 'Hebrew',
            native_name: 'עברית',
            direction: 'rtl',
            active: true,
            flag_icon: 'il',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          }
        ];
        console.log('[useLanguages] Using fallback languages due to error');
        setLanguages(fallbackLanguages);
      } finally {
        setLoading(false);
      }
    }

    fetchLanguages();
  }, [activeOnly]);

  return { languages, loading, error };
}
